import {I18n} from 'aws-amplify/utils';
import {
    Alert,
    Drawer,
    Loader,
    ScrollArea,
    Text,
} from '@mantine/core';

import {CertificateRequestForm} from '../forms/CertificateRequestForm';
import {emptyCertificateFormValues} from '../forms/certificateFormValues';
import {useRequestCertificateMutation} from '../hooks/useCertificateMutations';
import {useCertificateRequestConnections} from '../hooks/useCertificateRequestConnections';
import {RequestCertificateInput} from '../types';

// FNP-1193 — the request drawer.
//
// ⚠️ THERE IS NO EDIT COUNTERPART, and that is the schema's design rather than
// an omission here. A certificate is IMMUTABLE once issued — there is no
// `updateCertificate` mutation — so the write surface is request and delete.
// Nothing in this module may offer an edit affordance.
export const CertificateRequestDrawer = (
    {
        opened,
        onClose,
    }: {
        opened: boolean,
        onClose: () => void,
    },
) => {

    const {
        connections,
        loading,
        isError,
        hasAnyConnection,
    } = useCertificateRequestConnections();

    const requestMutation = useRequestCertificateMutation();

    const handleSubmit = async (input: RequestCertificateInput) => {
        // ⚠️ AWAITED, AND THE REJECTION PROPAGATES. The drawer closes only when
        // this resolves, so a refused request — the tenant's role missing the
        // ACM grant, above all — leaves the drawer open with the operator's
        // typing intact.
        await requestMutation.mutateAsync(input);
        onClose();
    };

    return (
        <Drawer
            position={'right'}
            offset={10}
            opened={opened}
            onClose={onClose}
            title={I18n.get('Request certificate')}
            padding={'xl'}
            size={'lg'}
            scrollAreaComponent={ScrollArea.Autosize}
            transitionProps={{
                transition: 'slide-left',
                duration: 750,
                timingFunction: 'ease',
            }}
        >
            {/* Remounted per open, so the form never shows the previous
                attempt's values or a stale validation error. */}
            {opened && (
                <>
                    {loading && <Loader size={'sm'}/>}

                    {isError && (
                        <Alert color={'red'} title={I18n.get('Could not load your connections')}>
                            {I18n.get('A certificate is issued through one of your provider connections, and the list of them could not be read. This is not a problem with the connections themselves — the request failed.')}
                        </Alert>
                    )}

                    {/* ⚠️ TWO DIFFERENT EMPTY STATES, because they have
                        different fixes. "You have no connections" means connect
                        a provider; "none of them can write" means reconnect one
                        with a credential that can. One message for both sends
                        half the tenants to the wrong place. */}
                    {!loading && !isError && connections.length === 0 && (
                        <Alert
                            color={'yellow'}
                            title={hasAnyConnection
                                ? I18n.get('No connection here can issue a certificate')
                                : I18n.get('No provider connections yet')}
                        >
                            <Text size={'sm'}>
                                {hasAnyConnection
                                    ? I18n.get('A certificate is issued in your own account under a connection whose role may write, and none of your connections is declared able to. Reconnect a provider declaring write access, and make sure its role carries acm:RequestCertificate.')
                                    : I18n.get('Connect a DNS provider first. The certificate is issued under that connection\'s credential, and its zone carries the validation record.')}
                            </Text>
                        </Alert>
                    )}

                    {!loading && !isError && connections.length > 0 && (
                        <CertificateRequestForm
                            initialValues={emptyCertificateFormValues(
                                // One usable connection is preselected — with
                                // exactly one there is no choice to make, and
                                // an empty required picker in that case is
                                // friction for nothing.
                                connections.length === 1 ? connections[0].id : undefined,
                            )}
                            connections={connections}
                            isSubmitting={requestMutation.isPending}
                            onSubmit={handleSubmit}
                            onCancel={onClose}
                        />
                    )}
                </>
            )}
        </Drawer>
    );
};
